import { Typography, Grid, Alert, Divider } from '@mui/material'
import BigNumber from 'bignumber.js'
import { NumberFieldC } from 'components'
import {
  useLoadOrderInstallments,
  useNoteTypesAllowZeroInstallmentsSet
} from 'modules/orders/hooks'
import { useOrderStore } from 'modules/orders/store'
import { Fragment, memo, useEffect } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { formatCurrency, useDebounce } from 'utils'
import { SelectPaymentMethods } from '../../SelectPaymentMethods'
import { useLocation } from 'react-router-dom'
import { blockedInstallmentPaymentTerm } from './config'
import { distributeInstallmentsProportionally } from './distributeInstallmentsProportionally'

const Installment = memo(function Installment({
  index,
  loading
}: {
  index: number
  loading: boolean
}) {
  const { watch } = useFormContext()
  const [watchFieldArray, paymentTerm] = watch(['installments', 'paymentTerm'])

  const blockLastInstallment = paymentTerm?.id === blockedInstallmentPaymentTerm

  return (
    <>
      <Grid item xs={12}>
        <Divider textAlign="left">
          <Typography variant="h6">Parcela {Number(index) + 1}</Typography>
        </Divider>
      </Grid>

      <Grid item xs={6}>
        <NumberFieldC
          name={`installments.${index}.value`}
          disabled={
            !watchFieldArray[index]?.paymentMethod?.isAdvance ||
            (blockLastInstallment && index === watchFieldArray?.length - 1)
          }
          label="Valor"
          prefix="R$ "
          loading={loading}
          normalize={(value) => value?.replace(',', '.')}
        />
      </Grid>
      <Grid item xs={6}>
        <SelectPaymentMethods
          name={`installments.${index}.paymentMethod`}
          isAdvance={+paymentTerm.hasAnticipation > 0 && index === 0}
        />
      </Grid>
    </>
  )
})

export default function InstallmentsView({ disabled }: any) {
  const form = useOrderStore.useForm()

  const { setValue, watch, setError, clearErrors } = useFormContext()

  const pageLocation = useLocation()
  const isEdit = pageLocation.pathname.includes('edit')
  const isDuplicatePage = pageLocation.pathname.includes('/duplicate')

  const [watchFieldArray, orderType] =
    watch(['installments', 'orderType']) || []

  const { allowsZeroInstallments } = useNoteTypesAllowZeroInstallmentsSet()
  const allowZeroInstallments = allowsZeroInstallments(orderType?.id)

  const { fields, append, remove } = useFieldArray({
    name: 'installments'
  })

  const controlledFields = fields.map((field, index) => {
    return {
      ...field,
      ...watchFieldArray[index]
    }
  })

  const valueDeb = useDebounce(watchFieldArray?.[0]?.value, 400)

  const fieldsLen = watchFieldArray?.length

  const { data, isFetching } = useLoadOrderInstallments({
    entry: watchFieldArray?.[0]?.paymentMethod?.isAdvance ? valueDeb : 0,
    paymentTermId: watch('paymentTerm')?.id,
    total: String(form?.total?.total || 0)
  })

  const total = form?.total?.total || 0

  const totalFromFields =
    controlledFields.reduce(
      (acc, cur) => BigNumber(acc).plus(cur.value).toNumber(),
      0
    ) || 0

  const isTotalEqual = total === totalFromFields

  useEffect(() => {
    if (isDuplicatePage) return
    if (total <= 0) return
    if (!fieldsLen) return
    if (Math.abs(total - totalFromFields) <= 0.01) return

    // The order total no longer matches the installments already stored in
    // the form (e.g. an item was edited on another step while this
    // component was unmounted). Clear them so the next fetch recalculates
    // every value from scratch, instead of feeding a stale "entrada" value
    // back as `entry` and only adjusting the remaining installments.
    remove()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total])

  const resolveInstallmentValue = (
    index: number,
    installmentCount: number,
    apiInstallments: any[],
    totalDiffers: boolean,
    currentInstallments: any[],
    duplicateSnapshot: any[]
  ) => {
    const apiInstallment = apiInstallments[index]
    const installmentValue = apiInstallment?.value ?? 0

    if (!totalDiffers) return installmentValue

    if (installmentCount === 1) return total

    if (index === installmentCount - 1) {
      const sumWithoutLast = Array.from(
        { length: installmentCount - 1 },
        (_, j) => {
          if (j < apiInstallments.length && apiInstallments[j]?.value != null) {
            return Number(apiInstallments[j].value)
          }

          return Number(
            currentInstallments[j]?.value ?? duplicateSnapshot[j]?.value ?? 0
          )
        }
      ).reduce((acc, cur) => acc + cur, 0)

      return total - sumWithoutLast
    }

    if (
      index < apiInstallments.length &&
      apiInstallments[index]?.value != null
    ) {
      return Number(apiInstallments[index].value)
    }

    return Number(
      currentInstallments[index]?.value ?? duplicateSnapshot[index]?.value ?? 0
    )
  }

  useEffect(() => {
    if (!data?.data) return

    const responseTotal = data.data.reduce(
      (acc: number, cur: any) => acc + (cur.value || 0),
      0
    )

    const totalDiffers = Math.abs(responseTotal - total) > 0.01

    const currentInstallments = watch('installments') || []
    const duplicateSnapshot = form?.duplicateInstallmentsSnapshot ?? []

    const resolvePaymentMethod = (index: number) => {
      const fromForm = currentInstallments[index]?.paymentMethod
      if (fromForm?.id) return fromForm

      if (isDuplicatePage) {
        return duplicateSnapshot[index]?.paymentMethod ?? null
      }

      return null
    }

    const shouldPreserveDuplicateInstallments =
      isDuplicatePage &&
      (duplicateSnapshot.length > 0 || currentInstallments.length > 0)

    if (shouldPreserveDuplicateInstallments) {
      const installmentCount = Math.max(
        currentInstallments.length,
        duplicateSnapshot.length
      )

      if (!installmentCount) return

      for (let index = 0; index < installmentCount; index++) {
        const apiInstallment = data.data[index] || {}
        const existingPaymentMethod = resolvePaymentMethod(index)
        const installmentValue = resolveInstallmentValue(
          index,
          installmentCount,
          data.data,
          totalDiffers,
          currentInstallments,
          duplicateSnapshot
        )

        const baseInstallment =
          currentInstallments[index] || duplicateSnapshot[index] || {}

        if (!controlledFields[index]) {
          append({
            ...baseInstallment,
            days: apiInstallment.days ?? baseInstallment.days,
            dueDate: apiInstallment.dueDate ?? baseInstallment.dueDate,
            value: installmentValue,
            paymentMethod: existingPaymentMethod
          })
          continue
        }

        const newValue = {
          ...baseInstallment,
          days: apiInstallment.days ?? baseInstallment.days,
          dueDate: apiInstallment.dueDate ?? baseInstallment.dueDate,
          value: installmentValue,
          paymentMethod: existingPaymentMethod
        }

        setValue(`installments.${index}`, newValue, {
          shouldValidate: !!newValue.paymentMethod
        })
      }

      return
    }

    const distributedValues = totalDiffers
      ? distributeInstallmentsProportionally(
          data.data.map((installment: any) => installment.value || 0),
          total
        )
      : null

    data.data.forEach((installment, index) => {
      const existingPaymentMethod = resolvePaymentMethod(index)

      const installmentValue = distributedValues
        ? distributedValues[index]
        : installment.value

      if (!controlledFields[index]) {
        append({
          ...installment,
          value: installmentValue,
          paymentMethod: existingPaymentMethod
        })
      } else {
        const currentInstallment = currentInstallments[index] || {}
        const newValue = {
          ...currentInstallment,
          days: installment.days,
          dueDate: installment.dueDate,
          value: installmentValue,
          paymentMethod: currentInstallment.paymentMethod ?? null
        }

        const shouldValidate = !!newValue.paymentMethod
        setValue(`installments.${index}`, newValue, { shouldValidate })
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [append, setValue, remove, data, total, isDuplicatePage])

  useEffect(() => {
    if (fieldsLen === 1 && total > 0) {
      const currentValue = watchFieldArray?.[0]?.value
      if (Math.abs(Number(currentValue || 0) - total) > 0.01) {
        setValue(`installments.0.value`, total, { shouldValidate: true })
      }
    }
  }, [fieldsLen, setValue, total, watchFieldArray])

  useEffect(() => {
    // Para orçamentos de consignação (tipo de nota "J"),
    // permitimos parcelas zeradas, ou seja, não bloqueamos o envio apenas
    // porque o total das parcelas é diferente do total do pedido.
    if (!allowZeroInstallments) {
      if (!isTotalEqual) {
        setError('installments', {
          message: 'total de parcelas deve ser igual ao total do pedido'
        })
      } else {
        clearErrors('installments')
      }
    } else {
      clearErrors('installments')
    }
    setValue('totalInstallments', totalFromFields)
  }, [
    clearErrors,
    allowZeroInstallments,
    isTotalEqual,
    setError,
    setValue,
    totalFromFields
  ])
  return (
    <>
      <Grid item xs={12}>
        <Alert severity={isTotalEqual ? 'success' : 'error'}>
          TOTAL: {formatCurrency(total)} / {formatCurrency(totalFromFields)}
        </Alert>
      </Grid>
      {isEdit ? (
        <Grid item xs={12}>
          <Alert severity="warning">
            Por favor, antes de avançar verifique se o Tipo de Pagamento da
            Parcela 1 está correto
          </Alert>
        </Grid>
      ) : (
        ''
      )}
      {/* <Grid item xs={12}>
        <Button
          onClick={() => setValue('installments.0.value', 0)}
          disabled={disabled}
        >
          Recalcular parcelas
        </Button>
      </Grid> */}

      {!disabled &&
        controlledFields.map((field, index) => (
          <Fragment key={field.id}>
            <Installment index={index} loading={isFetching} />
          </Fragment>
        ))}
    </>
  )
}
